/**
 * Context panel: whatever is selected, this is where you change it.
 *
 * One panel rather than scattered popovers — on a canvas tool, floating editors
 * fight the thing you are trying to look at. The *quick* actions on a table
 * (rotate, duplicate, remove) moved onto the canvas beside the table itself,
 * because those are gestures; what is left here is editing, which is typing.
 *
 * With nothing selected the panel answers the questions left: is this ready to
 * print, and is it safe? The checklist is the same set of facts the export
 * dialog would discover; the backup is the only copy of the plan that is not in
 * this browser.
 */

import { AnimatePresence, motion } from 'framer-motion'
import { useEffect, useRef, useState } from 'react'

import { useDraftText } from '../hooks/useDraftText'
import { TOUCH_FIRST, matchesMedia } from '../hooks/useMediaQuery'
import { type BackupMark, api } from '../lib/api'
import type { AlignEdge, DistributeAxis } from '../lib/arrange'
import { TABLE_COLORS, avatarTint, initialsOf, screenSwatchFor } from '../lib/colors'
import { FEATURES } from '../lib/config'
import { guestListCsv } from '../lib/csv'
import { isCalendarDate } from '../lib/dates'
import { fileSafe, saveBlob } from '../lib/download'
import { EVENT_KINDS } from '../lib/events'
import { MIN_SEATS, maxSeatCount } from '../lib/geometry'
import { chairsStillNeeded } from '../lib/guests'
import type { Catalogue } from '../lib/i18n'
import { useDateFormat, useT } from '../lib/i18n/I18nProvider'
import { messageForError } from '../lib/i18n/errors'
import { shapeName } from '../lib/i18n/shapeName'
import { swapTables } from '../lib/placing'
import type { EventKind, Guest, Plan, Table, TableColor } from '../lib/types'
import {
  guestSeatIndex,
  guestsUnseatedBySeatCount,
  seatedGuestIds,
  usePlanStore,
} from '../store/planStore'
import { useViewStore } from '../store/viewStore'
import { SwapIcon } from './PlacingBanner'

interface Props {
  plan: Plan
  onExport: () => void
  onBackup: () => void
  backingUp: boolean
  /** The plan's last backup, once read — `mark` undefined when there has been none. */
  backupMark: { mark: BackupMark | undefined } | null
}

export function Inspector(props: Props) {
  return (
    <aside className="inspector scroll-y">
      <InspectorBody {...props} />
    </aside>
  )
}

/** The panel's contents without its column — also the compact editor's selection sheet. */
export function InspectorBody({ plan, onExport, onBackup, backingUp, backupMark }: Props) {
  const activeSeat = usePlanStore((s) => s.activeSeat)
  const selectedIds = usePlanStore((s) => s.selectedTableIds)

  const seatTable = activeSeat ? plan.tables.find((t) => t.id === activeSeat.tableId) : null
  const seat = seatTable?.seats.find((s) => s.id === activeSeat?.seatId) ?? null
  const selectedTables = plan.tables.filter((table) => selectedIds.includes(table.id))

  let body: JSX.Element
  let key: string

  if (seat && seatTable) {
    key = `seat-${seat.id}`
    body = <SeatInspector plan={plan} table={seatTable} seatId={seat.id} />
  } else if (selectedTables.length === 1) {
    key = `table-${selectedTables[0].id}`
    body = <TableInspector table={selectedTables[0]} />
  } else if (selectedTables.length > 1) {
    key = 'multi'
    body = <MultiInspector tables={selectedTables} />
  } else {
    key = 'plan'
    body = (
      <PlanInspector
        plan={plan}
        onExport={onExport}
        onBackup={onBackup}
        backingUp={backingUp}
        backupMark={backupMark}
      />
    )
  }

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={key}
        initial={{ opacity: 0, x: 6 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: -6 }}
        transition={{ duration: 0.14, ease: [0.23, 1, 0.32, 1] }}
      >
        {body}
      </motion.div>
    </AnimatePresence>
  )
}

// --- table -------------------------------------------------------------------

function TableInspector({ table }: { table: Table }) {
  const t = useT()
  const renameTable = usePlanStore((s) => s.renameTable)
  const setTableColor = usePlanStore((s) => s.setTableColor)
  const addSeat = usePlanStore((s) => s.addSeat)
  const removeSeat = usePlanStore((s) => s.removeSeat)
  const setTableRotation = usePlanStore((s) => s.setTableRotation)
  const beginTransaction = usePlanStore((s) => s.beginTransaction)
  const unseatTables = usePlanStore((s) => s.unseatTables)
  const showToast = usePlanStore((s) => s.showToast)
  const hasOtherTables = usePlanStore((s) => (s.plan?.tables.length ?? 0) > 1)
  const eventKind = usePlanStore((s) => s.plan?.eventKind ?? 'wedding')

  // Buffered, so a table can be renamed to "Head Table" — writing every
  // keystroke to the document made the space between the words untypeable.
  const name = useDraftText(table.name, (value) => renameTable(table.id, value))

  const occupied = table.seats.filter((seat) => seat.guestId).length
  // Per shape: a banquet table only gets longer, so it seats more than a round.
  const ceiling = maxSeatCount(table.shape)
  const atMax = table.seatCount >= ceiling
  const atMin = table.seatCount <= MIN_SEATS

  return (
    <>
      <header className="inspector-head">
        <span className="eyebrow">{shapeName(t, table.shape, eventKind)}</span>
        <input
          className="inspector-title-input"
          {...name.bind}
          maxLength={80}
          aria-label={t.inspector.tableName}
          placeholder={t.inspector.tableName}
        />
        <p className="inspector-sub">{t.inspector.chairsTaken(occupied, table.seatCount)}</p>
      </header>

      <section className="inspector-section">
        <span className="label">{t.inspector.tableColour}</span>
        <div className="swatch-row">
          {TABLE_COLORS.map((color) => (
            <ColorSwatch
              key={color}
              color={color}
              active={table.color === color}
              onSelect={() => setTableColor(table.id, color)}
            />
          ))}
        </div>
      </section>

      <section className="inspector-section">
        <span className="label">{t.inspector.chairs}</span>
        <div className="stepper">
          <button
            type="button"
            className="stepper-btn"
            onClick={() => removeSeat(table.id)}
            disabled={atMin}
            aria-label={t.inspector.removeChair}
            title={atMin ? t.inspector.atMinChairs : t.inspector.removeChair}
          >
            −
          </button>
          <span className="stepper-value">
            <strong className="mono">{table.seatCount}</strong>
            <small>{t.inspector.chairsNoun}</small>
          </span>
          <button
            type="button"
            className="stepper-btn"
            onClick={() => addSeat(table.id)}
            disabled={atMax}
            aria-label={t.inspector.addChair}
            title={atMax ? t.inspector.atMaxChairs(ceiling) : t.inspector.addChair}
          >
            +
          </button>
        </div>
        <p className="inspector-hint">
          {atMax ? t.inspector.chairsAtCeiling(ceiling) : t.inspector.chairsCeiling(ceiling)}
        </p>
        {/* Clearing a whole table is one gesture, not one per chair. No
            confirmation: the label says the cost out loud and ⌘Z puts them
            all back where they were. */}
        <button
          type="button"
          className="btn btn-outline inspector-wide"
          disabled={occupied === 0}
          onClick={() => {
            const freed = unseatTables([table.id])
            if (freed > 0) showToast(t.inspector.unseatedToList(freed), 'info')
          }}
        >
          {occupied === 0 ? t.inspector.unseatAllEmpty : t.inspector.unseatAll(occupied)}
        </button>
        {/* Moving a whole table is a pick, not a form: the next table tapped is
            the other side. Enabled on an empty table too — that pulls the
            other table's guests over here. */}
        <button
          type="button"
          className="btn btn-outline inspector-wide"
          disabled={!hasOtherTables}
          onClick={() => useViewStore.getState().startSwap(table.id)}
        >
          <SwapIcon />
          {t.swap.action}
        </button>
      </section>

      <section className="inspector-section">
        <div className="inspector-row">
          <span className="label" style={{ marginBottom: 0 }}>
            {t.inspector.rotation}
          </span>
          <span className="mono inspector-value">{Math.round(table.rotation)}°</span>
        </div>
        <input
          type="range"
          className="slider"
          min={0}
          max={359}
          step={1}
          value={Math.round(table.rotation)}
          onPointerDown={() => beginTransaction()}
          // The keyboard path never fires pointerdown, and `setTableRotation`
          // is history-less (it runs at drag frequency) — without this, an
          // arrow-key rotation would be invisible to undo.
          onKeyDown={(event) => {
            const adjusts =
              event.key.startsWith('Arrow') ||
              event.key === 'Home' ||
              event.key === 'End' ||
              event.key === 'PageUp' ||
              event.key === 'PageDown'
            if (adjusts && !event.repeat) beginTransaction()
          }}
          onChange={(event) => setTableRotation(table.id, Number(event.target.value))}
          aria-label={t.inspector.rotation}
        />
        <div className="inspector-row inspector-row--tight">
          {[0, 45, 90, 135].map((angle) => (
            <button
              key={angle}
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() => {
                beginTransaction()
                setTableRotation(table.id, angle)
              }}
            >
              {angle}°
            </button>
          ))}
        </div>
      </section>
    </>
  )
}

function ColorSwatch({
  color,
  active,
  onSelect,
}: {
  color: TableColor
  active: boolean
  onSelect: () => void
}) {
  const swatch = screenSwatchFor(color)
  return (
    <button
      type="button"
      className={`swatch${active ? ' is-active' : ''}`}
      onClick={onSelect}
      aria-pressed={active}
      aria-label={swatch.label}
      title={swatch.label}
    >
      <span className="swatch-dot" style={{ background: swatch.surface, borderColor: swatch.edge }}>
        <span className="swatch-seat" style={{ background: swatch.accent }} />
      </span>
      <span className="swatch-name">{swatch.label}</span>
    </button>
  )
}

// --- seat / guest -------------------------------------------------------------

function SeatInspector({ plan, table, seatId }: { plan: Plan; table: Table; seatId: string }) {
  const t = useT()
  const seat = table.seats.find((s) => s.id === seatId)
  const guest = seat?.guestId ? plan.guests.find((g) => g.id === seat.guestId) : null

  const seatNewGuest = usePlanStore((s) => s.seatNewGuest)
  const updateGuest = usePlanStore((s) => s.updateGuest)
  const deleteGuest = usePlanStore((s) => s.deleteGuest)
  const clearSeat = usePlanStore((s) => s.clearSeat)
  const removeSeat = usePlanStore((s) => s.removeSeat)
  const setActiveSeat = usePlanStore((s) => s.setActiveSeat)
  const compact = useViewStore((s) => s.compact)

  const nameRef = useRef<HTMLInputElement>(null)

  // Focus the name field when an empty chair is opened — the overwhelmingly
  // common next action is typing a name. Not on a touch screen: there, focus
  // throws a keyboard over the room, and the next action is as often picking
  // someone from the waiting list below.
  useEffect(() => {
    if (!guest && !matchesMedia(TOUCH_FIRST)) nameRef.current?.focus()
  }, [guest, seatId])

  if (!seat) return null

  const submitName = (event: React.FormEvent) => {
    event.preventDefault()
    const value = nameRef.current?.value.trim()
    if (!value) return
    seatNewGuest({ tableId: table.id, seatId }, value)
    if (nameRef.current) nameRef.current.value = ''
  }

  return (
    <>
      <header className="inspector-head">
        <span className="eyebrow">{t.rail.seatAt(table.name, seat.position + 1)}</span>
        <h3 className="inspector-title">{guest ? guest.fullName : t.inspector.emptyChair}</h3>
      </header>

      {guest ? (
        <GuestEditor
          key={guest.id}
          plan={plan}
          guest={guest}
          onChange={(patch) => updateGuest(guest.id, patch)}
          onUnseat={() => {
            clearSeat({ tableId: table.id, seatId })
            setActiveSeat(null)
          }}
          onDelete={() => deleteGuest(guest.id)}
          onMove={() => useViewStore.getState().startPlacing(guest.id)}
        />
      ) : (
        <section className="inspector-section">
          <form onSubmit={submitName} className="guest-add">
            <input
              ref={nameRef}
              className="field"
              placeholder={t.inspector.whoSitsHere}
              aria-label={t.inspector.guestName}
              maxLength={120}
            />
            <button type="submit" className="btn btn-primary">
              {t.inspector.seatGuest}
            </button>
          </form>
          <WaitingGuests plan={plan} seat={{ tableId: table.id, seatId }} />
          {/* The list on the left only exists beside a wide canvas. */}
          {!compact && <p className="inspector-hint">{t.inspector.orDragSomeone}</p>}
        </section>
      )}

      <section className="inspector-section inspector-actions">
        <button
          type="button"
          className="btn btn-danger"
          onClick={() => removeSeat(table.id, seatId)}
          disabled={table.seatCount <= MIN_SEATS}
        >
          {t.inspector.removeThisChair}
        </button>
      </section>
    </>
  )
}

/**
 * Who is still waiting for a chair, for an empty one to take in a tap.
 *
 * Dragging a name from the list onto a chair needs the list and the chair on
 * screen at once, which a phone does not have. From the chair's side it is one
 * list and one tap. Searchable once the list is long enough to need it.
 */
function WaitingGuests({ plan, seat }: { plan: Plan; seat: { tableId: string; seatId: string } }) {
  const t = useT()
  const seatGuest = usePlanStore((s) => s.seatGuest)
  const [query, setQuery] = useState('')

  const seated = guestSeatIndex(plan)
  const waiting = plan.guests.filter((guest) => !seated.has(guest.id))
  if (waiting.length === 0) {
    return plan.guests.length > 0 ? <p className="inspector-hint">{t.rail.everyoneHasChair}</p> : null
  }

  const needle = query.trim().toLowerCase()
  const shown = needle
    ? waiting.filter(
        (guest) =>
          guest.fullName.toLowerCase().includes(needle) ||
          (guest.groupLabel ?? '').toLowerCase().includes(needle),
      )
    : waiting

  return (
    <div className="waiting">
      <span className="label waiting-label">
        {t.inspector.waitingForSeat} · {waiting.length}
      </span>
      {waiting.length > 6 && (
        <input
          className="field waiting-search"
          placeholder={t.rail.searchGuests}
          aria-label={t.rail.searchGuestsLabel}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />
      )}
      <ul className="waiting-list">
        {shown.map((guest) => (
          <li key={guest.id}>
            <button type="button" className="waiting-guest" onClick={() => seatGuest(guest.id, seat)}>
              <span className="guest-avatar" style={{ background: avatarTint(guest.id) }}>
                {guest.photoUrl ? <img src={guest.photoUrl} alt="" draggable={false} /> : initialsOf(guest.fullName)}
              </span>
              <span className="guest-chip-text">
                <strong>{guest.fullName}</strong>
                {guest.groupLabel && <small>{guest.groupLabel}</small>}
              </span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  )
}

function GuestEditor({
  plan,
  guest,
  onChange,
  onUnseat,
  onDelete,
  onMove,
}: {
  plan: Plan
  guest: Guest
  onChange: (patch: Partial<Omit<Guest, 'id'>>) => void
  onUnseat: () => void
  onDelete: () => void
  /** Pick them up for a tap on another chair — dragging a chair-sized target is for a mouse. */
  onMove: () => void
}) {
  const t = useT()
  const fileRef = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const showToast = usePlanStore((s) => s.showToast)
  // Buffered like a table's name. Written per keystroke, a cleared field saved
  // a guest named "" — which every later backup was then refused for.
  const name = useDraftText(guest.fullName, (value) => {
    const next = value.trim().slice(0, 120)
    if (!next) name.begin(guest.fullName)
    else if (next !== guest.fullName) onChange({ fullName: next })
  })

  const upload = async (file: File | undefined) => {
    if (!file) return
    setError(null)
    setUploading(true)
    try {
      const asset = await api.uploadPhoto(plan.id, file)
      onChange({ photoAssetId: asset.id, photoUrl: asset.url })
      showToast(t.inspector.photoAdded, 'success')
    } catch (err) {
      setError(messageForError(t, err, t.errors.uploadFailed))
    } finally {
      setUploading(false)
      if (fileRef.current) fileRef.current.value = ''
    }
  }

  return (
    <>
      <section className="inspector-section">
        <span className="label">{t.inspector.photo}</span>
        <div className="photo-editor">
          <button
            type="button"
            className={`photo-drop${uploading ? ' is-busy' : ''}`}
            onClick={() => fileRef.current?.click()}
            onDragOver={(event) => event.preventDefault()}
            onDrop={(event) => {
              event.preventDefault()
              void upload(event.dataTransfer.files?.[0])
            }}
            aria-label={guest.photoUrl ? t.inspector.replacePhoto : t.inspector.addPhoto}
          >
            {guest.photoUrl ? (
              <img src={guest.photoUrl} alt="" className="photo-preview" />
            ) : (
              <span className="photo-placeholder">
                <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
                  <path
                    d="M12 15.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7z M4 7h3l1.5-2h7L17 7h3v12H4V7z"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    strokeLinejoin="round"
                  />
                </svg>
                {t.inspector.addPhoto}
              </span>
            )}
            {uploading && <span className="photo-spinner" aria-label={t.inspector.uploading} />}
          </button>

          <div className="photo-actions">
            <button
              type="button"
              className="btn btn-outline btn-sm"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
            >
              {guest.photoUrl ? t.inspector.replace : t.inspector.upload}
            </button>
            {guest.photoUrl && (
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() => onChange({ photoAssetId: null, photoUrl: null })}
              >
                {t.inspector.removePhoto}
              </button>
            )}
            <p className="inspector-hint">{t.inspector.dropImage}</p>
          </div>
        </div>
        {error && <p className="inspector-error">{error}</p>}
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          hidden
          onChange={(event) => void upload(event.target.files?.[0])}
        />
      </section>

      <section className="inspector-section">
        <label className="label" htmlFor="guest-name">
          {t.inspector.guestNameLabel}
        </label>
        <input id="guest-name" className="field" {...name.bind} maxLength={120} />
      </section>

      <section className="inspector-section">
        <label className="label" htmlFor="guest-group">
          {t.inspector.groupLabel}
        </label>
        <input
          id="guest-group"
          className="field"
          value={guest.groupLabel ?? ''}
          placeholder={t.inspector.groupPlaceholder}
          onChange={(event) => onChange({ groupLabel: event.target.value || null })}
          maxLength={60}
        />
        <p className="inspector-hint">{t.inspector.groupHint}</p>
      </section>

      <section className="inspector-section">
        <span className="label">{t.inspector.rsvpLabel}</span>
        <div className="segmented">
          {(['yes', 'pending', 'no'] as const).map((value) => (
            <button
              key={value}
              type="button"
              className={`segmented-item${guest.rsvp === value ? ' is-active' : ''}`}
              onClick={() => onChange({ rsvp: value })}
              aria-pressed={guest.rsvp === value}
            >
              {value === 'yes'
                ? t.inspector.rsvpComing
                : value === 'no'
                  ? t.inspector.rsvpCant
                  : t.inspector.rsvpPending}
            </button>
          ))}
        </div>
        {/* The editor never empties a chair on its own — a decline can arrive
            from another tab mid-edit. It says so, and offers the one click. */}
        {guest.rsvp === 'no' && (
          <p className="inspector-declined">
            <span className="inspector-declined-mark" aria-hidden="true" />
            {t.inspector.declinedButSeated}
            <button type="button" className="btn btn-link btn-sm" onClick={onUnseat}>
              {t.inspector.freeTheChair}
            </button>
          </p>
        )}
      </section>

      <section className="inspector-section">
        <label className="label" htmlFor="guest-note">
          {t.inspector.noteLabel}
        </label>
        <textarea
          id="guest-note"
          className="field"
          value={guest.note ?? ''}
          placeholder={t.inspector.notePlaceholder}
          onChange={(event) => onChange({ note: event.target.value || null })}
          maxLength={2000}
        />
      </section>

      <section className="inspector-section">
        <button type="button" className="btn btn-outline inspector-wide" onClick={onMove}>
          {t.inspector.moveToChair}
        </button>
      </section>

      <section className="inspector-section inspector-actions">
        <button type="button" className="btn btn-outline" onClick={onUnseat}>
          {t.inspector.unseat}
        </button>
        <button type="button" className="btn btn-danger" onClick={onDelete}>
          {t.inspector.deleteGuest}
        </button>
      </section>
    </>
  )
}

// --- multi-select & plan -----------------------------------------------------

/**
 * Several tables at once.
 *
 * The panel mirrors the single-table one — colour, chairs, rotation — because a
 * selection is not a different kind of object, it is more of the same one. What
 * it adds is arrangement, which only means anything with more than one table
 * selected, and the wholesale clear-out that a re-plan usually starts with.
 *
 * Every action is one transaction, so recolouring or aligning nine tables is
 * one press of ⌘Z rather than nine.
 */
function MultiInspector({ tables }: { tables: Table[] }) {
  const t = useT()
  const setTableColor = usePlanStore((s) => s.setTableColor)
  const deleteTables = usePlanStore((s) => s.deleteTables)
  const transaction = usePlanStore((s) => s.transaction)
  const beginTransaction = usePlanStore((s) => s.beginTransaction)
  const unseatTables = usePlanStore((s) => s.unseatTables)
  const setSeatCountFor = usePlanStore((s) => s.setSeatCountFor)
  const setRotationFor = usePlanStore((s) => s.setRotationFor)
  const settleTables = usePlanStore((s) => s.settleTables)
  const alignTables = usePlanStore((s) => s.alignTables)
  const distributeTables = usePlanStore((s) => s.distributeTables)
  const showToast = usePlanStore((s) => s.showToast)

  const ids = tables.map((table) => table.id)
  const seated = tables.reduce(
    (sum, table) => sum + table.seats.filter((seat) => seat.guestId).length,
    0,
  )

  // "Mixed" is a real state, not a missing value: the selection genuinely has
  // no one chair count, and pretending otherwise by showing the first table's
  // would be a lie the stepper then acts on.
  const counts = new Set(tables.map((table) => table.seatCount))
  const sharedCount = counts.size === 1 ? [...counts][0] : null
  const angles = new Set(tables.map((table) => Math.round(table.rotation)))
  const sharedAngle = angles.size === 1 ? [...angles][0] : null

  // The cap is per shape, so "everyone is at the top" means every table is at
  // *its own* ceiling — a round table at 13 beside a banquet table at 17 is.
  const atMax = tables.every((table) => table.seatCount >= maxSeatCount(table.shape))
  const atMin = tables.every((table) => table.seatCount <= MIN_SEATS)

  /**
   * Step from the largest count in the selection, which converges a mixed one
   * onto a single number — which is the point of a bulk chair control.
   *
   * Read from the store rather than from `tables`: presses can outrun renders,
   * and stepping from a prop captured one render ago makes the second press of
   * a fast double-click a no-op.
   */
  const stepChairs = (delta: number) => {
    const live = usePlanStore.getState().plan?.tables.filter((table) => ids.includes(table.id))
    if (!live || live.length === 0) return
    applyChairs(Math.max(...live.map((table) => table.seatCount)) + delta)
  }

  const applyChairs = (count: number) => {
    const plan = usePlanStore.getState().plan
    const atRisk = guestsUnseatedBySeatCount(plan, ids, count)
    if (atRisk > 0 && !confirm(t.tableSettings.confirmUnseat(atRisk))) return
    const result = setSeatCountFor(ids, count)
    if (result.tables === 0) return
    showToast(
      result.unseated > 0
        ? t.tableSettings.appliedWithUnseated(result.tables, result.unseated)
        : t.tableSettings.applied(result.tables),
      result.unseated > 0 ? 'warn' : 'success',
    )
  }

  return (
    <>
      <header className="inspector-head">
        <span className="eyebrow">{t.inspector.multipleSelection}</span>
        <h3 className="inspector-title">{t.inspector.tablesSelected(tables.length)}</h3>
        <p className="inspector-sub">
          {t.inspector.chairsInTotal(tables.reduce((sum, table) => sum + table.seatCount, 0))}
        </p>
      </header>

      <section className="inspector-section">
        <span className="label">{t.inspector.setColourForAll}</span>
        <div className="swatch-row">
          {TABLE_COLORS.map((color) => (
            <ColorSwatch
              key={color}
              color={color}
              active={tables.every((table) => table.color === color)}
              onSelect={() =>
                // One transaction: recolouring five tables must be one ⌘Z, not five.
                transaction(() => {
                  for (const table of tables) setTableColor(table.id, color)
                })
              }
            />
          ))}
        </div>
      </section>

      <section className="inspector-section">
        <span className="label">{t.inspector.chairsForAll}</span>
        <div className="stepper">
          <button
            type="button"
            className="stepper-btn"
            onClick={() => stepChairs(-1)}
            disabled={atMin}
            aria-label={t.inspector.removeChair}
            title={atMin ? t.inspector.atMinChairs : t.inspector.removeChair}
          >
            −
          </button>
          <span className="stepper-value">
            {sharedCount === null ? (
              <strong className="mono">{t.inspector.chairsMixed}</strong>
            ) : (
              <>
                <strong className="mono">{sharedCount}</strong>
                <small>{t.inspector.chairsNoun}</small>
              </>
            )}
          </span>
          <button
            type="button"
            className="stepper-btn"
            onClick={() => stepChairs(1)}
            disabled={atMax}
            aria-label={t.inspector.addChair}
            title={t.inspector.addChair}
          >
            +
          </button>
        </div>
        <p className="inspector-hint">{t.inspector.chairsPerShapeHint}</p>
      </section>

      <section className="inspector-section">
        <div className="inspector-row">
          <span className="label" style={{ marginBottom: 0 }}>
            {t.inspector.turnAll}
          </span>
          <span className="mono inspector-value">
            {sharedAngle === null ? t.inspector.chairsMixed : `${sharedAngle}°`}
          </span>
        </div>
        <input
          type="range"
          className="slider"
          min={0}
          max={359}
          step={1}
          value={sharedAngle ?? 0}
          // One snapshot for the whole drag, then settle once on release —
          // the per-frame path is history-less, like the single-table slider.
          onPointerDown={() => beginTransaction()}
          onPointerUp={() => settleTables(ids)}
          onKeyDown={(event) => {
            const adjusts =
              event.key.startsWith('Arrow') ||
              event.key === 'Home' ||
              event.key === 'End' ||
              event.key === 'PageUp' ||
              event.key === 'PageDown'
            if (adjusts && !event.repeat) beginTransaction()
          }}
          onChange={(event) => setRotationFor(ids, Number(event.target.value), { transient: true })}
          aria-label={t.inspector.turnAll}
        />
        <div className="inspector-row inspector-row--tight">
          {[0, 45, 90, 135].map((angle) => (
            <button
              key={angle}
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() => setRotationFor(ids, angle)}
            >
              {angle}°
            </button>
          ))}
        </div>
      </section>

      <section className="inspector-section">
        <span className="label">{t.inspector.arrange}</span>
        {/* Each row is one axis and ends with its own "even the gaps", so the
            grid reads as across-then-down rather than as eight loose buttons. */}
        <div className="arrange-grid">
          {(['left', 'centerX', 'right'] as AlignEdge[]).map((edge) => (
            <ArrangeButton
              key={edge}
              label={ALIGN_LABEL(t, edge)}
              icon={ALIGN_ICONS[edge]}
              onClick={() => alignTables(ids, edge)}
            />
          ))}
          <ArrangeButton
            label={t.inspector.spreadAcross}
            icon={SPREAD_ICONS.x}
            disabled={tables.length < 3}
            onClick={() => distributeTables(ids, 'x')}
          />
          {(['top', 'centerY', 'bottom'] as AlignEdge[]).map((edge) => (
            <ArrangeButton
              key={edge}
              label={ALIGN_LABEL(t, edge)}
              icon={ALIGN_ICONS[edge]}
              onClick={() => alignTables(ids, edge)}
            />
          ))}
          <ArrangeButton
            label={t.inspector.spreadDown}
            icon={SPREAD_ICONS.y}
            disabled={tables.length < 3}
            onClick={() => distributeTables(ids, 'y')}
          />
        </div>
        {tables.length < 3 && <p className="inspector-hint">{t.inspector.spreadNeedsThree}</p>}
      </section>

      {/* Stacked, not side by side: "Unseat 14 guests" is a sentence, and
          sharing a row with "Delete 7 tables" clipped both. */}
      <section className="inspector-section inspector-actions inspector-actions--stack">
        {tables.length === 2 && (
          <button
            type="button"
            className="btn btn-outline"
            disabled={seated === 0}
            onClick={() => swapTables(ids[0], ids[1], t)}
          >
            <SwapIcon />
            {t.swap.pair}
          </button>
        )}
        <button
          type="button"
          className="btn btn-outline"
          disabled={seated === 0}
          onClick={() => {
            const freed = unseatTables(ids)
            if (freed > 0) showToast(t.inspector.unseatedToList(freed), 'info')
          }}
        >
          {seated === 0 ? t.inspector.unseatAllEmpty : t.inspector.unseatAll(seated)}
        </button>
        <button
          type="button"
          className="btn btn-danger"
          onClick={() => deleteTables(tables.map((table) => table.id))}
        >
          {t.inspector.deleteTables(tables.length)}
        </button>
      </section>
    </>
  )
}

function ArrangeButton({
  label,
  icon,
  disabled,
  onClick,
}: {
  label: string
  icon: JSX.Element
  disabled?: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      className="btn btn-ghost btn-icon"
      onClick={onClick}
      disabled={disabled}
      title={label}
      aria-label={label}
    >
      {icon}
    </button>
  )
}

const ALIGN_LABEL = (t: Catalogue, edge: AlignEdge): string =>
  ({
    left: t.inspector.alignLeft,
    centerX: t.inspector.alignCenterX,
    right: t.inspector.alignRight,
    top: t.inspector.alignTop,
    centerY: t.inspector.alignCenterY,
    bottom: t.inspector.alignBottom,
  })[edge]

/**
 * Each icon is a reference line plus two bars of unequal length, because that
 * is what the button does: the line is where the edges end up, and the bars
 * differing is what says it aligns edges rather than centres.
 */
function arrangeIcon(children: JSX.Element): JSX.Element {
  return (
    <svg viewBox="0 0 18 18" width="15" height="15" aria-hidden="true">
      {children}
    </svg>
  )
}

const RULE = { stroke: 'currentColor', strokeWidth: 1.6, strokeLinecap: 'round' } as const

const ALIGN_ICONS: Record<AlignEdge, JSX.Element> = {
  left: arrangeIcon(
    <>
      <path d="M2.5 2.5v13" {...RULE} />
      <rect x="5" y="4.2" width="10" height="3.2" rx="1.1" fill="currentColor" />
      <rect x="5" y="10.6" width="6" height="3.2" rx="1.1" fill="currentColor" />
    </>,
  ),
  centerX: arrangeIcon(
    <>
      <path d="M9 2.5v13" {...RULE} />
      <rect x="3" y="4.2" width="12" height="3.2" rx="1.1" fill="currentColor" />
      <rect x="5.5" y="10.6" width="7" height="3.2" rx="1.1" fill="currentColor" />
    </>,
  ),
  right: arrangeIcon(
    <>
      <path d="M15.5 2.5v13" {...RULE} />
      <rect x="3" y="4.2" width="10" height="3.2" rx="1.1" fill="currentColor" />
      <rect x="7" y="10.6" width="6" height="3.2" rx="1.1" fill="currentColor" />
    </>,
  ),
  top: arrangeIcon(
    <>
      <path d="M2.5 2.5h13" {...RULE} />
      <rect x="4.2" y="5" width="3.2" height="10" rx="1.1" fill="currentColor" />
      <rect x="10.6" y="5" width="3.2" height="6" rx="1.1" fill="currentColor" />
    </>,
  ),
  centerY: arrangeIcon(
    <>
      <path d="M2.5 9h13" {...RULE} />
      <rect x="4.2" y="3" width="3.2" height="12" rx="1.1" fill="currentColor" />
      <rect x="10.6" y="5.5" width="3.2" height="7" rx="1.1" fill="currentColor" />
    </>,
  ),
  bottom: arrangeIcon(
    <>
      <path d="M2.5 15.5h13" {...RULE} />
      <rect x="4.2" y="3" width="3.2" height="10" rx="1.1" fill="currentColor" />
      <rect x="10.6" y="7" width="3.2" height="6" rx="1.1" fill="currentColor" />
    </>,
  ),
}

/** Three bars at equal spacing — the gaps are the point, so they are the icon. */
const SPREAD_ICONS: Record<DistributeAxis, JSX.Element> = {
  x: arrangeIcon(
    <>
      <rect x="2" y="4" width="3" height="10" rx="1.1" fill="currentColor" />
      <rect x="7.5" y="4" width="3" height="10" rx="1.1" fill="currentColor" />
      <rect x="13" y="4" width="3" height="10" rx="1.1" fill="currentColor" />
    </>,
  ),
  y: arrangeIcon(
    <>
      <rect x="4" y="2" width="10" height="3" rx="1.1" fill="currentColor" />
      <rect x="4" y="7.5" width="10" height="3" rx="1.1" fill="currentColor" />
      <rect x="4" y="13" width="10" height="3" rx="1.1" fill="currentColor" />
    </>,
  ),
}

export function PlanInspector({
  plan,
  onExport,
  onBackup,
  backingUp,
  backupMark,
}: {
  plan: Plan
  onExport: () => void
  onBackup: () => void
  backingUp: boolean
  backupMark: { mark: BackupMark | undefined } | null
}) {
  const t = useT()
  const formatDate = useDateFormat()
  const setPlanDetails = usePlanStore((s) => s.setPlanDetails)

  const seated = plan.tables.reduce(
    (sum, table) => sum + table.seats.filter((seat) => seat.guestId).length,
    0,
  )
  const chairs = plan.tables.reduce((sum, table) => sum + table.seatCount, 0)
  // Someone who cannot come is not a chair the plan still owes: the checklist
  // would otherwise never go green on a wedding with a single decline.
  const unseated = chairsStillNeeded(plan.guests, seatedGuestIds(plan))
  const hasDetails = Boolean(plan.eventDate) && Boolean(plan.venue)
  const kindWords = t.events.kinds[plan.eventKind]

  const downloadGuestList = () => {
    const csv = guestListCsv(plan, t)
    const name = `${fileSafe(plan.title, 'yerbeyer')}-${t.csv.fileSuffix}.csv`
    saveBlob(new Blob([csv], { type: 'text/csv;charset=utf-8' }), name)
  }

  return (
    <>
      <span className="eyebrow inspector-eyebrow">{t.inspector.thisPlan}</span>
      {/* Not printed: it chose the starter room and still names the head table,
          the date and the venue below. Changing it moves no furniture. */}
      <section className="inspector-section">
        <label className="label-plain" htmlFor="plan-kind">
          {t.events.label}
        </label>
        <select
          id="plan-kind"
          className="field"
          value={plan.eventKind}
          onChange={(event) => setPlanDetails({ eventKind: event.target.value as EventKind })}
        >
          {EVENT_KINDS.map((kind) => (
            <option key={kind} value={kind}>
              {t.events.kinds[kind].name}
            </option>
          ))}
        </select>
      </section>
      <div className="stat-grid">
        <Stat label={t.inspector.statTables} value={plan.tables.length} />
        <Stat label={t.inspector.statChairs} value={chairs} />
        <Stat label={t.inspector.statGuests} value={plan.guests.length} />
        <Stat label={t.inspector.statSeated} value={seated} tone={unseated === 0 ? 'good' : null} />
      </div>

      <span className="eyebrow inspector-eyebrow">{t.inspector.printedOnPdf}</span>
      <section className="inspector-section">
        <label className="label-plain" htmlFor="plan-date">
          {kindWords.dateLabel}
        </label>
        <input
          id="plan-date"
          type="date"
          className="field"
          value={plan.eventDate ?? ''}
          max="9999-12-31"
          onChange={(event) => {
            // A browser's date field takes six-digit years; a backup refuses them.
            const value = event.target.value
            if (!value || isCalendarDate(value)) setPlanDetails({ eventDate: value || null })
          }}
        />
      </section>

      <section className="inspector-section">
        <label className="label-plain" htmlFor="plan-venue">
          {t.inspector.venue}
        </label>
        <input
          id="plan-venue"
          className="field"
          value={plan.venue ?? ''}
          placeholder={kindWords.venuePlaceholder}
          onChange={(event) => setPlanDetails({ venue: event.target.value || null })}
          maxLength={200}
        />
      </section>

      {/* The checklist states what *is* true as often as what is missing: a row
          that only ever appears when something is wrong turns the panel into a
          nag, and "everyone has a chair" is worth reading. */}
      <div className="checklist">
        <p className="checklist-title">{t.inspector.readyToPrint}</p>
        <ul className="checklist-rows">
          <ChecklistRow
            ok={plan.tables.length > 0}
            label={
              plan.tables.length > 0
                ? t.inspector.checkHasTables(plan.tables.length)
                : t.inspector.checkNoTables
            }
          />
          <ChecklistRow
            ok={unseated === 0}
            label={unseated === 0 ? t.inspector.checkSeated : t.inspector.checkUnseated(unseated)}
          />
          <ChecklistRow
            ok={hasDetails}
            label={hasDetails ? t.inspector.checkDetails : t.inspector.checkDetailsMissing}
          />
        </ul>
        {FEATURES.pdfExport && (
          <>
            <button type="button" className="btn btn-outline checklist-cta" onClick={onExport}>
              {t.inspector.downloadPdf}
            </button>
            <p className="inspector-hint">{t.inspector.downloadPdfHint}</p>
          </>
        )}
        <button
          type="button"
          className="btn btn-outline checklist-cta"
          onClick={downloadGuestList}
          disabled={plan.guests.length === 0}
        >
          {t.inspector.downloadGuestList}
        </button>
        <p className="inspector-hint">{t.inspector.downloadGuestListHint}</p>
      </div>

      <span className="eyebrow inspector-eyebrow">{t.inspector.backupTitle}</span>
      <section className="inspector-section">
        <p className="inspector-hint inspector-hint--flush">{t.inspector.backupHint}</p>
        {backupMark && (
          <p
            className={`inspector-backup-state${
              !backupMark.mark || plan.version > backupMark.mark.version ? ' is-stale' : ''
            }`}
          >
            {!backupMark.mark
              ? t.dashboard.neverBackedUp
              : plan.version > backupMark.mark.version
                ? t.dashboard.changedSinceBackup(formatDate(backupMark.mark.at))
                : t.dashboard.backedUp(formatDate(backupMark.mark.at))}
          </p>
        )}
        <button
          type="button"
          className="btn btn-outline checklist-cta"
          onClick={onBackup}
          disabled={backingUp}
        >
          {t.inspector.downloadBackup}
        </button>
      </section>
    </>
  )
}

function Stat({ label, value, tone }: { label: string; value: number; tone?: 'good' | null }) {
  return (
    <div className="stat">
      <strong className={tone === 'good' ? 'is-good' : undefined}>{value}</strong>
      <span>{label}</span>
    </div>
  )
}

function ChecklistRow({ ok, label }: { ok: boolean; label: string }) {
  return (
    <li className={`checklist-row${ok ? ' is-ok' : ''}`}>
      <span className="checklist-mark" aria-hidden="true" />
      {label}
    </li>
  )
}
